/**
 * 🔴 **会话存储 —— token + 当前账本**
 *
 * ## 为什么"当前账本"要存在客户端
 *
 * 架构 §6.2：`X-Ledger-Id` 是账本上下文的**唯一来源**，且**不得从 JWT 推导**
 * （1 用户 ≥ 1 账本 + 有账本切换器 ⇒ "当前账本"是**请求级上下文**，不是会话属性）。
 * ⇒ 它必须由客户端记住并随每次请求发出。本文件就是那个"记住"。
 *
 * ## ⚠️ 边界（诚实登记）
 *
 * 这是 P0 的最小实现：`localStorage` 明文存 token。**风险与后续动作**：
 * - XSS 可读 ⇒ 生产应改为 **httpOnly cookie 或至少短时 token + 刷新**；
 * - 但本产品是**单人/家庭自用 PWA**，且没有第三方脚本 ⇒ P0 接受，**登记为 P1 待办**。
 * 🔴 **不要**因为"方便"把 `ledgerId` 也塞进 token —— 那会锁死账本切换（架构 §6.2 已论证）。
 */

import type { Session } from './http.js';

const KEY = 'jiaying.session.v1';

/** 读会话（未登录 / 解析失败 ⇒ `null`，**不抛** —— 坏数据不该让整个应用白屏） */
export function loadSession(): Session | null {
  const raw = globalThis.localStorage?.getItem(KEY);
  if (raw === null || raw === undefined || raw === '') return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (
      typeof parsed.token !== 'string'
      || typeof parsed.ledgerId !== 'string'
      || parsed.token === ''
      || parsed.ledgerId === ''
    ) {
      return null;
    }
    return {
      token: parsed.token,
      ledgerId: parsed.ledgerId,
      // 🆕 批次 `66`：老会话（升级前写的）没有 userId ⇒ `''`。
      // 🔴 空串的语义 = "不知道我是谁" ⇒ 实时提示条**一律当别人的操作**（`features/realtime-toast.ts`）。
      //    宁可多弹一次，也不静默吞掉别人的改动 —— 提示条是"协作感知"，漏报比误报严重。
      userId: typeof parsed.userId === 'string' ? parsed.userId : '',
      userName: typeof parsed.userName === 'string' ? parsed.userName : '',
      ledgerName: typeof parsed.ledgerName === 'string' ? parsed.ledgerName : '',
    };
  } catch {
    return null;
  }
}

export function saveSession(session: Session): void {
  globalThis.localStorage?.setItem(KEY, JSON.stringify(session));
}

export function clearSession(): void {
  globalThis.localStorage?.removeItem(KEY);
}
